import React, { useCallback } from 'react';
import gql from 'graphql-tag';
import { useLazyQuery } from '@apollo/client';

import { InputDlg } from './input-dialog';
import { Form as FForm, Formik, useField, useFormikContext } from 'formik';
import {
  ButtonSearch,
  FieldInput,
  FormCol6,
  FilterProps,
  promiseModalSelect,
  FormRow,
  TableCellV8,
  FieldLabel,
} from 'motion-components';
import { Form, Col } from 'react-bootstrap';
import { t } from 'i18next';
import { FieldSelectCustomerType } from 'shared-pages';
import { useRootStore } from 'store';
// import { promiseModalSelect } from './modal-select';

interface Props {
  initValueId: string;
  initValueName: string;
  onValueChange: (valueId: string, valueName: string) => void;
  required?: boolean;
  initFilters?: any;
}

interface FFProps extends Props {
  label?: string;
  name?: string;
  as?: React.ElementType<any>;
}

/* -------------------------------------------------
   Select Customer with Modal Search and Label Formik Form
   ------------------------------------------------- */
export const FormFieldCustomerDlg = ({
  label = 'Φυσικό ή Νομικό Πρόσωπο',
  name = 'cstGuid',
  as = 'div',
  initValueId = undefined,
  initValueName = '',
  onValueChange,
  required = false,
  initFilters = {},
}: FFProps) => {
  const [{ value }, { touched, error, initialValue }] = useField<number>(name);
  const { setFieldValue, setFieldTouched } = useFormikContext<any>();

  const setFieldValueAndTouched = useCallback(
    async (_value, touched: boolean) => {
      //await Promise.all([setFieldValue(name, _value, true), setFieldTouched(name, touched)]);
      await setFieldValue(name, _value, true); //field,value,shouldValidate
      await setFieldTouched(name, touched); //field,touched,shouldValidate
    },
    [name, setFieldTouched, setFieldValue],
  );

  const handleChange = useCallback(
    (valueId: string, valueName: string) => {
      setFieldValueAndTouched(valueId, true);
      if (onValueChange) onValueChange(valueId, valueName);
    },
    [setFieldValueAndTouched],
  );

  const changed = value !== initialValue;

  return (
    <Form.Group as={as} controlId={name} className="mb-2">
      <FieldLabel text={label} changed={changed} required={required} />
      <div>
        <FieldCustomerDlg
          initValueId={initValueId}
          initValueName={initValueName}
          onValueChange={handleChange}
          initFilters={initFilters}
        />
        {touched && !!error && (
          <div className="text-danger select-feedback">
            <small>{error}</small>
          </div>
        )}
      </div>
    </Form.Group>
  );
};

/* ----------------------------------
   Select Customer with Modal Search
   ---------------------------------- */

export const FieldCustomerDlg = ({
  initValueId = undefined,
  initValueName = '',
  onValueChange,
  required = false,
  initFilters = {},
}: Props) => {
  const { appStore } = useRootStore();
  const valueChanged = (valueId, valueName) => {
    onValueChange(valueId, valueName);
  };

  return (
    <InputDlg
      valueId={initValueId}
      valueName={initValueName}
      onDlgClick={async (event) => {
        const res = await promiseModalSelectCustomer({ initFilters, appStore });
        return { newId: res?.guid, newName: res?.name };
      }}
      onValueChange={valueChanged}
    />
  );
};

// -------------------------------------------------------------------------------
// Modal Select Customer
// -------------------------------------------------------------------------------

const gqlGetCustomers = gql`
  query getCustomers($where: String, $offset: Int, $limit: Int, $order: String) {
    getCustomers(where: $where, offset: $offset, limit: $limit, order: $order) {
      data {
        guid
        nid
        name
        business
        cstTypes {
          guid
          cstType {
            guid
            name
          }
        }
      }
      recordCount
    }
  }
`;

const columns = [
  {
    id: 'name',
    header: 'Επωνυμία',
    columnName: 'Επωνυμία',
    accessorKey: 'name',
    orderByField: 'name',
    cell: TableCellV8,
    size: 120,
  },
  {
    id: 'business',
    header: 'Επάγγελμα',
    columnName: 'Επάγγελμα',
    accessorKey: 'business',
    orderByField: 'business',
    cell: TableCellV8,
    size: 120,
  },
  {
    id: 'cstTypes',
    header: 'Τύπος',
    columnName: 'Τύπος',
    accessorKey: 'cstTypes',
    orderByField: 'cstTypes',
    cell: (info) => {
      const tableMeta = info.table.options.meta;
      const row = info.row;
      const cstTypes = row.original.cstTypes.map((value) => value.cstType.name);
      const text = cstTypes.sort().join(', ');
      return <div>{text}</div>;
    },
    size: 60,
  },
];

// -------------------------------------------------------------------------------
// Filters
// -------------------------------------------------------------------------------
const buildWhereFromFilters = (values) => {
  // Φτιάξε το where για το graphql
  let where = {};
  if (values) {
    if (values.searchText)
      where = {
        ...where,
        ...{
          name: { $like: '%' + values.searchText + '%' },
        },
      };
    if (values.cstTypeGuid) {
      //    if (values.cstTypeGuid !== undefined && values.cstTypeGuid !== null) {
      where = { ...where, customerTypeGuid: values.cstTypeGuid };
    }
  }
  return where;
};

const Filters: (props: FilterProps) => JSX.Element = ({ onChange, initialValues }) => {
  return (
    <Formik initialValues={initialValues ? initialValues : { searchText: '' }} onSubmit={onChange}>
      <Form as={FForm}>
        <FormRow className="mb-3 align-items-end">
          <FieldInput
            as={FormCol6}
            name="searchText"
            id="searchText"
            label="Επωνυμία"
            type="search"
          />
          <Col className="pl-0 pr-0 me-3 ">
            <ButtonSearch size="sm" type="submit" className="float-right mb-2" />
          </Col>
        </FormRow>
        <FormRow className="mb-3 align-items-end">
          <FieldSelectCustomerType as={FormCol6} name="cstTypeGuid" label="Τύπος" />
        </FormRow>
      </Form>
    </Formik>
  );
};

// -------------------------------------------------------------------------------

export const promiseModalSelectCustomer = (props) => {
  return promiseModalSelect({
    ...props,
    title: 'Επιλογή Φυσικού ή Νομικού Προσώπου',
    columns,
    gqlQuery: gqlGetCustomers,
    buildWhereFromFilters: buildWhereFromFilters,
    initFilters: props.initFilters,
    Filters: Filters,
  });
};
// -------------------------------------------------------------------------------

export function useGetCustomerByGuid(guid) {
  return useLazyQuery(gqlGetCustomers, {
    variables: { where: JSON.stringify({ guid: guid }) },
    fetchPolicy: 'network-only',
  });
}

